import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { pushEnvVarsToMachines, getEcrServices } from '../api/ecrDeploymentsApi';
import { EcrDeploymentsContext } from '../contexts/EcrDeploymentsContext';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthContext } from '../../auth/contexts/AuthContext';

const useStyles = makeStyles((theme: any) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  resultCard: {
    marginBottom: theme.spacing(2),
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
  },
}));

interface PushResult {
  machine_serial: string;
  success: boolean;
  error: string | null;
  response: {
    service: string;
    vars_created: Array<{ key: string; value: string }>;
    vars_updated: Array<{ key: string; old_value: string; new_value: string }>;
    vars_unchanged: string[];
    persisted_to_file: boolean;
    applied_to_container: boolean;
    restarted: boolean;
  } | null;
}

interface ApiResponse {
  success: boolean;
  machines_processed: number;
  successful: number;
  failed: number;
  results: PushResult[];
}

interface ServiceConfig {
  id: string;
  service_name: string;
  env_vars_text: string;
  restart_service: boolean;
}

const PushEnvVars: React.FC = () => {
  const classes = useStyles();
  const { machines, fetchMachines } = React.useContext(EcrDeploymentsContext);
  const { getAccessTokenSilently } = useAuth0();
  const { accessToken } = React.useContext(AuthContext);
  
  const [selectedMachines, setSelectedMachines] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceConfigs, setServiceConfigs] = useState<ServiceConfig[]>([
    {
      id: '1',
      service_name: 'autobulk-api',
      env_vars_text: '',
      restart_service: false,
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getToken = async (): Promise<string> => {
    if (accessToken) return accessToken;
    return await getAccessTokenSilently();
  };

  // Load machines and services on mount
  useEffect(() => {
    fetchMachines();
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoadingServices(true);
    try {
      const token = await getToken();
      const response = await getEcrServices(token);
      setServices(response.services);
      if (response.services.length > 0 && serviceConfigs[0]) {
        setServiceConfigs([{ ...serviceConfigs[0], service_name: response.services[0] }]);
      }
    } catch (err) {
      console.error('Failed to load services:', err);
    } finally {
      setLoadingServices(false);
    }
  };

  const addServiceConfig = () => {
    const newId = (serviceConfigs.length + 1).toString();
    setServiceConfigs([
      ...serviceConfigs,
      {
        id: newId,
        service_name: services[0] || 'autobulk-api',
        env_vars_text: '',
        restart_service: false,
      },
    ]);
  };

  const removeServiceConfig = (id: string) => {
    setServiceConfigs(serviceConfigs.filter((config) => config.id !== id));
  };

  const updateServiceConfig = (id: string, updates: Partial<ServiceConfig>) => {
    setServiceConfigs(
      serviceConfigs.map((config) =>
        config.id === id ? { ...config, ...updates } : config
      )
    );
  };

  const handleSubmit = async () => {
    if (selectedMachines.length === 0) {
      setError('Please select at least one machine');
      return;
    }

    const hasEnvVars = serviceConfigs.some((config) => config.env_vars_text.trim());
    if (!hasEnvVars) {
      setError('Please enter environment variables for at least one service');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const token = await getToken();
      
      // Send requests for each service config
      const allResults: PushResult[] = [];
      for (const config of serviceConfigs) {
        if (!config.env_vars_text.trim()) continue; // Skip empty configs
        
        const response = await pushEnvVarsToMachines(token, {
          machine_serials: selectedMachines,
          service_name: config.service_name,
          env_vars_text: config.env_vars_text,
          restart_services: config.restart_service,
        });
        
        // Aggregate results
        allResults.push(...response.results);
      }

      // Create combined response
      const combinedResponse: ApiResponse = {
        success: allResults.some(r => r.success),
        machines_processed: selectedMachines.length,
        successful: allResults.filter(r => r.success).length,
        failed: allResults.filter(r => !r.success).length,
        results: allResults,
      };

      setResults(combinedResponse);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to push env vars');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Push Environment Variables to Machines
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Update environment variables on one or more machines. Changes are persisted to .env files and optionally applied to running containers.
      </Typography>

      <Paper className={classes.paper}>
        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            1. Select Machines
          </Typography>
          <Autocomplete
            multiple
            options={machines.map(m => m.serial_number)}
            value={selectedMachines}
            onChange={(_, newValue) => setSelectedMachines(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Machines"
                placeholder="Start typing to search machines..."
                helperText="Select one or more machines to update"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
          />
        </Box>

        <Box className={classes.section}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              2. Configure Services
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addServiceConfig}
              variant="outlined"
              size="small"
            >
              Add Service
            </Button>
          </Box>

          {serviceConfigs.map((config, index) => (
            <Card key={config.id} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Service</InputLabel>
                    <Select
                      value={config.service_name}
                      label="Service"
                      onChange={(e) => updateServiceConfig(config.id, { service_name: e.target.value })}
                      disabled={loadingServices}
                    >
                      {services.map((service) => (
                        <MenuItem key={service} value={service}>
                          {service}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Environment Variables"
                    multiline
                    rows={6}
                    fullWidth
                    value={config.env_vars_text}
                    onChange={(e) => updateServiceConfig(config.id, { env_vars_text: e.target.value })}
                    placeholder={'FEATURE_FLAG=true\nAPI_TIMEOUT=60'}
                    helperText="KEY=VALUE format, one per line"
                    sx={{ fontFamily: 'monospace', mb: 2 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.restart_service}
                        onChange={(e) => updateServiceConfig(config.id, { restart_service: e.target.checked })}
                      />
                    }
                    label="Restart this service after update"
                  />
                </Box>

                {serviceConfigs.length > 1 && (
                  <IconButton
                    onClick={() => removeServiceConfig(config.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Card>
          ))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          className={classes.button}
        >
          {loading ? <CircularProgress size={24} /> : 'Push to Machines'}
        </Button>
      </Paper>

      {results && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Results
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">
                  Machines Processed:
                </Typography>
                <Typography variant="h6">{results.machines_processed}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">
                  Successful:
                </Typography>
                <Typography variant="h6" color="success.main">
                  {results.successful}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">
                  Failed:
                </Typography>
                <Typography variant="h6" color="error.main">
                  {results.failed}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {results.results.map((result, index) => (
            <Card key={index} className={classes.resultCard}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {result.machine_serial}
                  </Typography>
                  <Chip
                    label={result.success ? 'Success' : 'Failed'}
                    className={result.success ? classes.successChip : classes.errorChip}
                  />
                </Box>

                {result.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {result.error}
                  </Alert>
                )}

                {result.response && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Service: {result.response.service}
                    </Typography>

                    {result.response.vars_created.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ➕ Created ({result.response.vars_created.length}):
                        </Typography>
                        {result.response.vars_created.map((v, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 2, fontFamily: 'monospace' }}>
                            {v.key} = {v.value}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {result.response.vars_updated.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="primary.main">
                          🔄 Updated ({result.response.vars_updated.length}):
                        </Typography>
                        {result.response.vars_updated.map((v, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 2, fontFamily: 'monospace' }}>
                            {v.key}: '{v.old_value}' → '{v.new_value}'
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {result.response.vars_unchanged.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          ✓ Unchanged ({result.response.vars_unchanged.length}):
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 2, fontFamily: 'monospace' }}>
                          {result.response.vars_unchanged.join(', ')}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Persisted to file: {result.response.persisted_to_file ? '✅' : '❌'} |
                        Applied to container: {result.response.applied_to_container ? '✅' : '❌'} |
                        Restarted: {result.response.restarted ? '✅' : '❌'}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default PushEnvVars;
